import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiX,
  FiCalendar,
  FiUsers,
  FiClock,
  FiMapPin,
  FiCheck,
  FiAlertCircle
} from 'react-icons/fi';
import api from '../../services/api';
import './AdminExams.css';

const AdminExams = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    examDate: '',
    examTime: '10:00',
    location: 'BMK Eğitim Kurumları',
    applicationStartDate: '',
    applicationEndDate: '',
    maxApplications: 0,
    targetGrades: ['5', '6', '7', '8', '9', '10', '11', '12'],
    scholarshipRates: [
      { rank: '1-10', rate: 100 },
      { rank: '11-50', rate: 75 },
      { rank: '51-100', rate: 50 },
      { rank: '101-200', rate: 25 }
    ]
  });

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      const response = await api.get('/exams');
      setExams(response.data.exams || []);
    } catch (error) {
      console.error('Sınavlar yüklenemedi:', error);
      toast.error('Sınavlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingExam) {
        await api.put(`/exams/${editingExam._id}`, formData);
        toast.success('Sınav güncellendi');
      } else {
        await api.post('/exams', formData);
        toast.success('Sınav oluşturuldu');
      }
      
      setShowModal(false);
      setEditingExam(null);
      resetForm();
      fetchExams();
    } catch (error) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleEdit = (exam) => {
    setEditingExam(exam);
    setFormData({
      title: exam.title,
      description: exam.description || '',
      examDate: exam.examDate?.split('T')[0] || '',
      examTime: exam.examTime || '10:00',
      location: exam.location || 'BMK Eğitim Kurumları',
      applicationStartDate: exam.applicationStartDate?.split('T')[0] || '',
      applicationEndDate: exam.applicationEndDate?.split('T')[0] || '',
      maxApplications: exam.maxApplications || 0,
      targetGrades: exam.targetGrades || ['5', '6', '7', '8', '9', '10', '11', '12'],
      scholarshipRates: exam.scholarshipRates || formData.scholarshipRates,
      isActive: exam.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu sınavı silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/exams/${id}`);
      toast.success('Sınav silindi');
      fetchExams();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      examDate: '',
      examTime: '10:00',
      location: 'BMK Eğitim Kurumları',
      applicationStartDate: '',
      applicationEndDate: '',
      maxApplications: 0,
      targetGrades: ['5', '6', '7', '8', '9', '10', '11', '12'],
      scholarshipRates: [
        { rank: '1-10', rate: 100 },
        { rank: '11-50', rate: 75 },
        { rank: '51-100', rate: 50 },
        { rank: '101-200', rate: 25 }
      ]
    });
  };

  const getStatusBadge = (exam) => {
    const status = exam.registrationStatus;
    const statusMap = {
      'open': { label: 'Başvuru Açık', class: 'status-open' },
      'upcoming': { label: 'Yakında', class: 'status-upcoming' },
      'closed': { label: 'Başvuru Kapandı', class: 'status-closed' },
      'full': { label: 'Kontenjan Doldu', class: 'status-full' },
      'completed': { label: 'Tamamlandı', class: 'status-completed' },
      'inactive': { label: 'Pasif', class: 'status-inactive' }
    };
    
    const info = statusMap[status] || { label: 'Bilinmiyor', class: 'status-unknown' };
    return <span className={`status-badge ${info.class}`}>{info.label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleGradeToggle = (grade) => {
    setFormData(prev => ({
      ...prev,
      targetGrades: prev.targetGrades.includes(grade)
        ? prev.targetGrades.filter(g => g !== grade)
        : [...prev.targetGrades, grade]
    }));
  };

  return (
    <div className="admin-exams">
      {/* Header */}
      <div className="exams-header">
        <div>
          <h2>Bursluluk Sınavları</h2>
          <p>Sınav ekleyin, düzenleyin ve başvuruları yönetin</p>
        </div>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          <FiPlus /> Yeni Sınav
        </button>
      </div>

      {/* Exam List */}
      {loading ? (
        <div className="exams-loading">
          <div className="spinner"></div>
          <p>Yükleniyor...</p>
        </div>
      ) : exams.length === 0 ? (
        <div className="exams-empty">
          <FiCalendar />
          <h3>Henüz sınav eklenmemiş</h3>
          <p>İlk bursluluk sınavınızı ekleyerek başlayın</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <FiPlus /> Sınav Ekle
          </button>
        </div>
      ) : (
        <div className="exams-grid">
          {exams.map((exam, index) => (
            <motion.div
              key={exam._id}
              className="exam-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="exam-card-header">
                <h3>{exam.title}</h3>
                {getStatusBadge(exam)}
              </div>
              
              <div className="exam-card-body">
                <div className="exam-info">
                  <div className="exam-info-item">
                    <FiCalendar />
                    <span>Sınav: {formatDate(exam.examDate)}</span>
                  </div>
                  <div className="exam-info-item">
                    <FiClock />
                    <span>Saat: {exam.examTime}</span>
                  </div>
                  <div className="exam-info-item">
                    <FiMapPin />
                    <span>{exam.location}</span>
                  </div>
                  <div className="exam-info-item">
                    <FiUsers />
                    <span>
                      Başvuru: {exam.currentApplications || 0}
                      {exam.maxApplications > 0 ? ` / ${exam.maxApplications}` : ''}
                    </span>
                  </div>
                </div>
                
                <div className="exam-dates">
                  <small>
                    Başvuru: {formatDate(exam.applicationStartDate)} - {formatDate(exam.applicationEndDate)}
                  </small>
                </div>
              </div>
              
              <div className="exam-card-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(exam)}>
                  <FiEdit2 /> Düzenle
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(exam._id)}>
                  <FiTrash2 /> Sil
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { setShowModal(false); setEditingExam(null); }}
          >
            <motion.div 
              className="modal-content modal-lg"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{editingExam ? 'Sınav Düzenle' : 'Yeni Sınav'}</h2>
                <button onClick={() => { setShowModal(false); setEditingExam(null); }}>
                  <FiX />
                </button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label className="form-label">Sınav Adı *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="2024 Bursluluk Sınavı"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Açıklama</label>
                    <textarea
                      className="form-textarea"
                      rows={2}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Sınav hakkında kısa bilgi..."
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Sınav Tarihi *</label>
                      <input
                        type="date"
                        className="form-input"
                        value={formData.examDate}
                        onChange={(e) => setFormData({ ...formData, examDate: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Sınav Saati</label>
                      <input
                        type="time"
                        className="form-input"
                        value={formData.examTime}
                        onChange={(e) => setFormData({ ...formData, examTime: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Sınav Yeri</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="BMK Eğitim Kurumları"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Başvuru Başlangıç *</label>
                      <input
                        type="date"
                        className="form-input"
                        value={formData.applicationStartDate}
                        onChange={(e) => setFormData({ ...formData, applicationStartDate: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Başvuru Bitiş *</label>
                      <input
                        type="date"
                        className="form-input"
                        value={formData.applicationEndDate}
                        onChange={(e) => setFormData({ ...formData, applicationEndDate: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Maksimum Başvuru (0 = Sınırsız)</label>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.maxApplications}
                      onChange={(e) => setFormData({ ...formData, maxApplications: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Hedef Sınıflar</label>
                    <div className="grade-options">
                      {['5', '6', '7', '8', '9', '10', '11', '12'].map(grade => (
                        <label key={grade} className="grade-option">
                          <input
                            type="checkbox"
                            checked={formData.targetGrades.includes(grade)}
                            onChange={() => handleGradeToggle(grade)}
                          />
                          <span>{grade}. Sınıf</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Burs Oranları</label>
                    <div className="scholarship-rates">
                      {formData.scholarshipRates.map((rate, index) => (
                        <div key={index} className="rate-item">
                          <input
                            type="text"
                            className="form-input"
                            value={rate.rank}
                            onChange={(e) => {
                              const newRates = [...formData.scholarshipRates];
                              newRates[index].rank = e.target.value;
                              setFormData({ ...formData, scholarshipRates: newRates });
                            }}
                            placeholder="Sıralama (1-10)"
                          />
                          <div className="rate-input-wrapper">
                            <input
                              type="number"
                              className="form-input"
                              value={rate.rate}
                              onChange={(e) => {
                                const newRates = [...formData.scholarshipRates];
                                newRates[index].rate = parseInt(e.target.value) || 0;
                                setFormData({ ...formData, scholarshipRates: newRates });
                              }}
                              min="0"
                              max="100"
                            />
                            <span>%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {editingExam && (
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.isActive !== false}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        />
                        <span>Sınav Aktif</span>
                      </label>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setEditingExam(null); }}>
                    İptal
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <FiCheck /> {editingExam ? 'Güncelle' : 'Oluştur'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminExams;

